// src/services/studentProfile.service.js
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  getDocs,
  collection,
} from "firebase/firestore";
import { db } from "@/firebase";
import { Logger } from "@/utils/logger";
import { isTestModeAllowed } from "@/config/testMode";
import {
  COLLECTIONS,
  ONBOARDING_STATUS,
  validateStudentProfile,
} from "./database.schema";
import { updateOnboardingStatus } from "./auth.service";

// Available instruments for student selection
export const INSTRUMENTS = [
  { value: "piano", label: "Piano" },
  { value: "guitar", label: "Guitar" },
  { value: "violin", label: "Violin" },
  { value: "drums", label: "Drums" },
  { value: "bass", label: "Bass Guitar" },
  { value: "saxophone", label: "Saxophone" },
  { value: "trumpet", label: "Trumpet" },
  { value: "flute", label: "Flute" },
  { value: "clarinet", label: "Clarinet" },
  { value: "cello", label: "Cello" },
  { value: "voice", label: "Voice/Singing" },
  { value: "ukulele", label: "Ukulele" },
  { value: "other", label: "Other" },
];

// Available genres for preference selection
export const GENRES = [
  { value: "classical", label: "Classical" },
  { value: "jazz", label: "Jazz" },
  { value: "rock", label: "Rock" },
  { value: "pop", label: "Pop" },
  { value: "country", label: "Country" },
  { value: "blues", label: "Blues" },
  { value: "folk", label: "Folk" },
  { value: "electronic", label: "Electronic" },
  { value: "r&b", label: "R&B" },
  { value: "hip_hop", label: "Hip Hop" },
  { value: "latin", label: "Latin" },
  { value: "world", label: "World Music" },
];

/**
 * Create a new student profile
 * @param {string} userId - User ID
 * @param {Object} profileData - Student profile data
 * @returns {Promise<boolean>} Success status
 */
export const createStudentProfile = async (userId, profileData) => {
  Logger.log(
    "info",
    "Creating student profile",
    { userId, primaryInstrument: profileData.primaryInstrument },
    "StudentProfileService"
  );

  try {
    // Validate the profile data
    const completeProfileData = {
      userId,
      onboardingCompleted: true,
      completedAt: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      ...profileData,
    };

    validateStudentProfile(completeProfileData);

    // Create the student profile document
    const profileDocRef = doc(db, COLLECTIONS.STUDENT_PROFILES, userId);
    await setDoc(profileDocRef, completeProfileData);

    // Update the user's onboarding status using auth service
    await updateOnboardingStatus(
      userId,
      ONBOARDING_STATUS.COMPLETED,
      new Date()
    );

    Logger.log(
      "info",
      "Student profile created successfully",
      { userId },
      "StudentProfileService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error creating student profile",
      { error: error.message, userId },
      "StudentProfileService"
    );
    throw error;
  }
};

/**
 * Create a "skipped" student profile.
 *
 * When a student skips onboarding we still mark onboarding complete (so they
 * aren't forced back into it), but we must NOT invent preferences. The previous
 * behaviour stored fabricated defaults (primaryInstrument "unknown", a made-up
 * budget, etc.) that then showed on the profile as if the student had chosen
 * them. This writes genuinely empty preferences and an `onboardingSkipped` flag;
 * the profile UI renders "Not set yet" prompts for the blank fields. It also
 * bypasses the full student-profile validator on purpose, since a skipped
 * profile is legitimately incomplete.
 *
 * @param {string} userId - User ID
 * @returns {Promise<boolean>}
 */
export const createSkippedStudentProfile = async (userId) => {
  Logger.log(
    "info",
    "Creating skipped (minimal) student profile",
    { userId },
    "StudentProfileService"
  );

  try {
    const profileData = {
      userId,
      onboardingCompleted: true,
      onboardingSkipped: true,
      completedAt: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      // Preferences intentionally left blank — shown as "Not set yet" prompts.
      primaryInstrument: "",
      skillLevel: "",
      musicalGoals: [],
      preferredGenres: [],
      availableHours: "",
      budgetRange: "",
      learningPreferences: {},
    };

    const profileDocRef = doc(db, COLLECTIONS.STUDENT_PROFILES, userId);
    await setDoc(profileDocRef, profileData);

    await updateOnboardingStatus(
      userId,
      ONBOARDING_STATUS.COMPLETED,
      new Date()
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error creating skipped student profile",
      { error: error.message, userId },
      "StudentProfileService"
    );
    throw error;
  }
};

/**
 * Get student profile by user ID
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Student profile or null if not found
 */
export const getStudentProfile = async (userId) => {
  Logger.log(
    "info",
    "Getting student profile",
    { userId },
    "StudentProfileService"
  );

  try {
    const profileDocRef = doc(db, COLLECTIONS.STUDENT_PROFILES, userId);
    const profileDoc = await getDoc(profileDocRef);

    if (profileDoc.exists()) {
      const profileData = profileDoc.data();
      Logger.log(
        "info",
        "Student profile retrieved successfully",
        { userId, hasProfile: true },
        "StudentProfileService"
      );
      return { id: profileDoc.id, ...profileData };
    }

    Logger.log(
      "info",
      "No student profile found",
      { userId },
      "StudentProfileService"
    );
    return null;
  } catch (error) {
    Logger.log(
      "error",
      "Error getting student profile",
      { error: error.message, userId },
      "StudentProfileService"
    );
    throw error;
  }
};

/**
 * Update student profile
 * @param {string} userId - User ID
 * @param {Object} profileData - Updated profile data
 * @returns {Promise<boolean>} Success status
 */
export const updateStudentProfile = async (userId, profileData) => {
  Logger.log(
    "info",
    "Updating student profile",
    { userId },
    "StudentProfileService"
  );

  try {
    const profileDocRef = doc(db, COLLECTIONS.STUDENT_PROFILES, userId);
    const updateData = {
      ...profileData,
      updatedAt: new Date(),
    };

    await updateDoc(profileDocRef, updateData);

    Logger.log(
      "info",
      "Student profile updated successfully",
      { userId },
      "StudentProfileService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error updating student profile",
      { error: error.message, userId },
      "StudentProfileService"
    );
    throw error;
  }
};

/**
 * Check if user has completed onboarding
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} Onboarding completion status
 */
export const hasCompletedOnboarding = async (userId) => {
  try {
    // Check for Cypress test mode (build-gated; never active in production)
    if (isTestModeAllowed()) {
      if (window.__mockStudentProfileService) {
        Logger.log(
          "debug",
          "Using mock student profile service in test mode",
          { userId },
          "StudentProfileService"
        );
        return await window.__mockStudentProfileService.hasCompletedOnboarding(
          userId
        );
      }

      // Fallback to checking mock user data directly
      const mockUser = window.__getAuthState ? window.__getAuthState() : null;
      if (mockUser && mockUser.uid === userId) {
        const completed = mockUser.onboardingStatus === "COMPLETED";
        Logger.log(
          "debug",
          "Checking mock user onboarding status",
          { userId, completed, onboardingStatus: mockUser.onboardingStatus },
          "StudentProfileService"
        );
        return completed;
      }

      // Check mock Firestore data
      if (
        window.firestoreMockData &&
        window.firestoreMockData.student_profiles
      ) {
        const profile = window.firestoreMockData.student_profiles[userId];
        if (profile) {
          const completed = profile.onboardingCompleted === true;
          Logger.log(
            "debug",
            "Using mock Firestore student profile data",
            { userId, completed },
            "StudentProfileService"
          );
          return completed;
        }
      }

      return false;
    }

    const profile = await getStudentProfile(userId);
    return profile && profile.onboardingCompleted === true;
  } catch (error) {
    Logger.log(
      "error",
      "Error checking onboarding status",
      { error: error.message, userId },
      "StudentProfileService"
    );
    return false;
  }
};

/**
 * Mark onboarding as complete when student visits teacher directly from onboarding
 * @param {string} userId - User ID
 * @param {Object} onboardingData - Data from onboarding session
 * @returns {Promise<boolean>} Success status
 */
export const completeOnboardingFromTeacherVisit = async (
  userId,
  onboardingData = {}
) => {
  Logger.log(
    "info",
    "Completing onboarding from teacher visit",
    { userId },
    "StudentProfileService"
  );

  try {
    // Check if profile already exists
    const existingProfile = await getStudentProfile(userId);

    if (existingProfile && existingProfile.onboardingCompleted) {
      Logger.log(
        "info",
        "Onboarding already completed",
        { userId },
        "StudentProfileService"
      );
      return true;
    }

    // Create or update profile with minimal data to mark onboarding complete
    const profileData = {
      onboardingCompleted: true,
      completedAt: new Date(),
      completedVia: "teacher_visit",
      ...onboardingData, // Include any onboarding data that was collected
    };

    if (existingProfile) {
      // Update existing profile
      await updateStudentProfile(userId, profileData);
    } else {
      // Create new profile with default values
      const defaultProfileData = {
        userId,
        primaryInstrument: onboardingData.instrument || "piano",
        skillLevel: onboardingData.skillLevel || "beginner",
        musicalGoals: onboardingData.musicalGoals || [],
        preferredGenres: onboardingData.genres || [],
        budgetRange: onboardingData.budget || "under_50",
        practiceTime: onboardingData.practiceHours || "less_than_1",
        lessonFormat: onboardingData.preferredFormat || ["online"],
        createdAt: new Date(),
        updatedAt: new Date(),
        ...profileData,
      };

      const profileDocRef = doc(db, COLLECTIONS.STUDENT_PROFILES, userId);
      await setDoc(profileDocRef, defaultProfileData);
    }

    // Update the user's onboarding status using auth service
    await updateOnboardingStatus(
      userId,
      ONBOARDING_STATUS.COMPLETED,
      new Date()
    );

    Logger.log(
      "info",
      "Onboarding marked complete from teacher visit",
      { userId },
      "StudentProfileService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error completing onboarding from teacher visit",
      { error: error.message, userId },
      "StudentProfileService"
    );
    throw error;
  }
};

/**
 * Get teacher recommendations using temporary onboarding data (for use during onboarding)
 * @param {Object} onboardingData - Temporary profile data from onboarding steps
 * @param {number} limit - Maximum number of recommendations (default: 5)
 * @returns {Promise<Array>} Array of recommended teachers
 */
export const getTeacherRecommendationsForOnboarding = async (
  onboardingData,
  limit = 5
) => {
  Logger.log(
    "debug",
    "Getting teacher recommendations for onboarding",
    { onboardingData, limit },
    "StudentProfileService"
  );

  try {
    // Use the provided onboarding data instead of fetching from Firestore
    const profile = {
      instrument: onboardingData.instrument,
      skillLevel: onboardingData.skillLevel,
      musicalGoals: onboardingData.musicalGoals || [],
      genres: onboardingData.genres || [],
      practiceHours: onboardingData.practiceHours,
      budget: onboardingData.budget,
      location: onboardingData.location || "",
      preferredFormat: onboardingData.preferredFormat || ["online"],
    };

    // Get all teachers
    const teachersRef = collection(db, COLLECTIONS.TEACHERS);
    const teachersSnapshot = await getDocs(teachersRef);

    if (teachersSnapshot.empty) {
      Logger.log(
        "warning",
        "No teachers found in database",
        {},
        "StudentProfileService"
      );
      return [];
    }

    const teachers = [];
    teachersSnapshot.forEach((doc) => {
      const teacherData = doc.data();
      teachers.push({
        id: doc.id,
        ...teacherData,
      });
    });

    // Score teachers based on profile matching
    const scoredTeachers = teachers.map((teacher) => {
      let score = 0;
      const matchReasons = [];

      // Primary instrument match (highest weight)
      if (
        teacher.instrument &&
        teacher.instrument.toLowerCase() === profile.instrument.toLowerCase()
      ) {
        score += 50;
        matchReasons.push(`Teaches ${profile.instrument}`);
      }

      // Skill level appropriate
      if (teacher.tags && Array.isArray(teacher.tags)) {
        const levelTags = teacher.tags.filter((tag) =>
          tag.toLowerCase().includes(profile.skillLevel.toLowerCase())
        );
        if (levelTags.length > 0) {
          score += 20;
          matchReasons.push(`Specializes in ${profile.skillLevel} students`);
        }
      }

      // Genre preferences match
      if (profile.genres && teacher.tags) {
        const genreMatches = profile.genres.filter((genre) =>
          teacher.tags.some((tag) =>
            tag.toLowerCase().includes(genre.toLowerCase())
          )
        );
        if (genreMatches.length > 0) {
          score += 15 * genreMatches.length;
          matchReasons.push(`Teaches ${genreMatches.join(", ")} music`);
        }
      }

      // Return teacher with score and reasons
      return {
        ...teacher,
        matchScore: score,
        matchReasons: matchReasons,
      };
    });

    // Filter and sort recommendations
    const recommendations = scoredTeachers
      .filter((teacher) => teacher.matchScore > 0)
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, limit);

    Logger.log(
      "info",
      "Teacher recommendations generated for onboarding",
      {
        recommendationCount: recommendations.length,
        topScore: recommendations[0]?.matchScore,
      },
      "StudentProfileService"
    );

    return recommendations;
  } catch (error) {
    Logger.log(
      "error",
      "Failed to get teacher recommendations for onboarding",
      {
        error: error.message,
        onboardingData,
      },
      "StudentProfileService"
    );
    throw error;
  }
};

/**
 * Get teacher recommendations based on student profile
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of recommendations (default: 5)
 * @returns {Promise<Array>} Array of recommended teachers
 */
export const getTeacherRecommendations = async (userId, limit = 5) => {
  Logger.log(
    "info",
    "Getting teacher recommendations",
    { userId, limit },
    "StudentProfileService"
  );

  try {
    const profile = await getStudentProfile(userId);
    if (!profile) {
      throw new Error("Student profile not found");
    }

    // Get all teachers
    const teachersRef = collection(db, COLLECTIONS.TEACHERS);
    const teachersSnapshot = await getDocs(teachersRef);

    const teachers = [];
    teachersSnapshot.forEach((doc) => {
      const teacherData = doc.data();
      teachers.push({ id: doc.id, ...teacherData });
    });

    // Score teachers based on profile matching
    const scoredTeachers = teachers.map((teacher) => {
      let score = 0;
      const matchReasons = [];

      // Primary instrument match (highest weight)
      if (
        teacher.instrument &&
        teacher.instrument.toLowerCase() ===
          profile.primaryInstrument.toLowerCase()
      ) {
        score += 50;
        matchReasons.push(`Teaches ${profile.primaryInstrument}`);
      }

      // Skill level appropriate (check if teacher has experience with student's level)
      if (teacher.tags && Array.isArray(teacher.tags)) {
        const levelTags = teacher.tags.filter((tag) =>
          tag.toLowerCase().includes(profile.skillLevel.toLowerCase())
        );
        if (levelTags.length > 0) {
          score += 20;
          matchReasons.push(`Specializes in ${profile.skillLevel} students`);
        }
      }

      // Genre preferences match
      if (profile.preferredGenres && teacher.tags) {
        const genreMatches = profile.preferredGenres.filter((genre) =>
          teacher.tags.some((tag) =>
            tag.toLowerCase().includes(genre.toLowerCase())
          )
        );
        if (genreMatches.length > 0) {
          score += 15 * genreMatches.length;
          matchReasons.push(`Teaches ${genreMatches.join(", ")} music`);
        }
      }

      // Budget compatibility
      if (teacher.hourlyRate && profile.budgetRange) {
        const budgetMatch = checkBudgetCompatibility(
          teacher.hourlyRate,
          profile.budgetRange
        );
        if (budgetMatch) {
          score += 15;
          matchReasons.push("Within your budget range");
        }
      }

      // Rating boost
      if (teacher.rating > 4.5) {
        score += 10;
        matchReasons.push("Highly rated teacher");
      }

      return {
        ...teacher,
        matchScore: score,
        matchReasons,
      };
    });

    // Sort by score and return top recommendations
    const recommendations = scoredTeachers
      .filter((teacher) => teacher.matchScore > 0)
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, limit);

    Logger.log(
      "info",
      "Teacher recommendations generated",
      { userId, recommendationCount: recommendations.length },
      "StudentProfileService"
    );

    return recommendations;
  } catch (error) {
    Logger.log(
      "error",
      "Error getting teacher recommendations",
      { error: error.message, userId },
      "StudentProfileService"
    );
    throw error;
  }
};

/**
 * Get resource suggestions using temporary onboarding data (for use during onboarding)
 * @param {Object} onboardingData - Temporary profile data from onboarding steps
 * @param {number} limit - Maximum number of suggestions (default: 10)
 * @returns {Promise<Array>} Array of suggested resources
 */
export const getResourceSuggestionsForOnboarding = async (
  onboardingData,
  limit = 10
) => {
  Logger.log(
    "debug",
    "Getting resource suggestions for onboarding",
    { onboardingData, limit },
    "StudentProfileService"
  );

  try {
    // Use the provided onboarding data instead of fetching from Firestore
    const profile = {
      instrument: onboardingData.instrument,
      skillLevel: onboardingData.skillLevel,
      musicalGoals: onboardingData.musicalGoals || [],
      genres: onboardingData.genres || [],
      practiceHours: onboardingData.practiceHours,
      budget: onboardingData.budget,
    };

    // Get all resources
    const resourcesRef = collection(db, COLLECTIONS.RESOURCES);
    const resourcesSnapshot = await getDocs(resourcesRef);

    if (resourcesSnapshot.empty) {
      Logger.log(
        "warning",
        "No resources found in database",
        {},
        "StudentProfileService"
      );
      return [];
    }

    const resources = [];
    resourcesSnapshot.forEach((doc) => {
      const resourceData = doc.data();
      resources.push({
        id: doc.id,
        ...resourceData,
      });
    });

    // Score resources based on profile matching
    const scoredResources = resources.map((resource) => {
      let score = 0;
      const matchReasons = [];

      // Instrument relevance
      if (
        resource.instrument &&
        resource.instrument.toLowerCase() === profile.instrument.toLowerCase()
      ) {
        score += 30;
        matchReasons.push(`For ${profile.instrument} students`);
      }

      // Skill level appropriateness
      if (
        resource.skillLevel &&
        resource.skillLevel.toLowerCase() === profile.skillLevel.toLowerCase()
      ) {
        score += 25;
        matchReasons.push(`Perfect for ${profile.skillLevel} level`);
      }

      // Goal alignment
      if (resource.category && profile.musicalGoals) {
        const goalMatch = profile.musicalGoals.some((goal) =>
          resource.category
            .toLowerCase()
            .includes(goal.replace("_", " ").toLowerCase())
        );
        if (goalMatch) {
          score += 20;
          matchReasons.push("Matches your musical goals");
        }
      }

      // Free resources get a small boost
      if (!resource.isPaid) {
        score += 15;
        matchReasons.push("Free resource");
      }

      // Return resource with score and reasons
      return {
        ...resource,
        matchScore: score,
        matchReasons: matchReasons,
      };
    });

    // Filter and sort suggestions
    const suggestions = scoredResources
      .filter((resource) => resource.matchScore > 0)
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, limit);

    Logger.log(
      "info",
      "Resource suggestions generated for onboarding",
      {
        suggestionCount: suggestions.length,
        topScore: suggestions[0]?.matchScore,
      },
      "StudentProfileService"
    );

    return suggestions;
  } catch (error) {
    Logger.log(
      "error",
      "Failed to get resource suggestions for onboarding",
      {
        error: error.message,
        onboardingData,
      },
      "StudentProfileService"
    );
    throw error;
  }
};

/**
 * Get resource suggestions based on student profile
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of suggestions (default: 10)
 * @returns {Promise<Array>} Array of suggested resources
 */
export const getResourceSuggestions = async (userId, limit = 10) => {
  Logger.log(
    "info",
    "Getting resource suggestions",
    { userId, limit },
    "StudentProfileService"
  );

  try {
    const profile = await getStudentProfile(userId);
    if (!profile) {
      throw new Error("Student profile not found");
    }

    // Get all resources
    const resourcesRef = collection(db, COLLECTIONS.RESOURCES);
    const resourcesSnapshot = await getDocs(resourcesRef);

    const resources = [];
    resourcesSnapshot.forEach((doc) => {
      const resourceData = doc.data();
      resources.push({ id: doc.id, ...resourceData });
    });

    // Score resources based on profile matching
    const scoredResources = resources.map((resource) => {
      let score = 0;
      const matchReasons = [];

      // Instrument relevance
      if (
        resource.instrument &&
        resource.instrument.toLowerCase() ===
          profile.primaryInstrument.toLowerCase()
      ) {
        score += 30;
        matchReasons.push(`For ${profile.primaryInstrument} students`);
      }

      // Skill level appropriateness
      if (
        resource.skillLevel &&
        resource.skillLevel.toLowerCase() === profile.skillLevel.toLowerCase()
      ) {
        score += 25;
        matchReasons.push(`Perfect for ${profile.skillLevel} level`);
      }

      // Goal alignment
      if (resource.category && profile.musicalGoals) {
        const goalMatch = profile.musicalGoals.some((goal) =>
          resource.category
            .toLowerCase()
            .includes(goal.replace("_", " ").toLowerCase())
        );
        if (goalMatch) {
          score += 20;
          matchReasons.push("Matches your musical goals");
        }
      }

      // Budget considerations (for paid resources)
      if (resource.isPaid && resource.price && profile.budgetRange) {
        if (resource.price <= getBudgetMax(profile.budgetRange)) {
          score += 10;
          matchReasons.push("Within your budget");
        }
      } else if (!resource.isPaid) {
        score += 15; // Free resources get a small boost
        matchReasons.push("Free resource");
      }

      // Popularity boost
      if (resource.purchaseCount > 50) {
        score += 5;
        matchReasons.push("Popular resource");
      }

      return {
        ...resource,
        matchScore: score,
        matchReasons,
      };
    });

    // Sort by score and return top suggestions
    const suggestions = scoredResources
      .filter((resource) => resource.matchScore > 0)
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, limit);

    Logger.log(
      "info",
      "Resource suggestions generated",
      { userId, suggestionCount: suggestions.length },
      "StudentProfileService"
    );

    return suggestions;
  } catch (error) {
    Logger.log(
      "error",
      "Error getting resource suggestions",
      { error: error.message, userId },
      "StudentProfileService"
    );
    throw error;
  }
};

// Helper function to check budget compatibility
const checkBudgetCompatibility = (hourlyRate, budgetRange) => {
  switch (budgetRange) {
    case "under_50":
      return hourlyRate < 50;
    case "50-100":
      return hourlyRate >= 50 && hourlyRate <= 100;
    case "100-200":
      return hourlyRate >= 100 && hourlyRate <= 200;
    case "200+":
      return hourlyRate >= 200;
    default:
      return false;
  }
};

// Helper function to get maximum budget value
const getBudgetMax = (budgetRange) => {
  switch (budgetRange) {
    case "under_50":
      return 49;
    case "50-100":
      return 100;
    case "100-200":
      return 200;
    case "200+":
      return 999;
    default:
      return 100;
  }
};

export default {
  INSTRUMENTS,
  GENRES,
  createStudentProfile,
  getStudentProfile,
  updateStudentProfile,
  hasCompletedOnboarding,
  completeOnboardingFromTeacherVisit,
  getTeacherRecommendations,
  getTeacherRecommendationsForOnboarding,
  getResourceSuggestions,
  getResourceSuggestionsForOnboarding,
};
